import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import { asDate, asString } from "common/components/component-helpers";
import { PastAndOrFuture } from "common/components/past-future";
import { ActionSource, LOADING } from "common/components/types";
import type { GlideFunctionalRendererProps } from "common/components/uis";
import { convertDateFromTimeZoneAgnostic, convertDateToTimeZoneAgnostic } from "common/Support";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import DateTimePicker from "webapp/ui/smart/date-time-picker/date-time-picker";

export type DateTimeVariant = "date" | "date-time";

export const BaseDateTimeFieldMaterialRenderer: React.FunctionComponent<
    GlideFunctionalRendererProps & { variant: DateTimeVariant }
> = p => {
    const { env, getValueProducers, ctx, variant } = p;
    const [force] = useForce();
    const [args, [, dateVP, , , , , validFlagVP]] = useForceValueProducers(getValueProducers, force, ctx);
    const [placeholder, value, title, onChangeValue, isEnabledValue, isRequiredValue, , , , pastAndOrFutureValue] =
        args;
    const { onClick: actionCB } = useOnClickFromAction(onChangeValue, ActionSource.DateTimeField, ctx, env);
    const [editingAllowed] = useEditComponentEnabledViaNetworkStatus(env);

    if (value === LOADING) return env.renderProgress();

    const onChange = async (newValue: Date | undefined) => {
        if (dateVP === undefined) return;

        const timeZoneAgnostic = definedMap(newValue, convertDateToTimeZoneAgnostic);

        dateVP.set(timeZoneAgnostic ?? "");
        if (actionCB !== undefined) {
            dontAwait(actionCB(undefined));
        }
    };

    const isEnabled = editingAllowed && (actionCB === undefined || isEnabledValue !== false);
    const isRequired = isRequiredValue === true;

    const dateVal = definedMap(asDate(value), convertDateFromTimeZoneAgnostic);

    if (validFlagVP !== undefined) {
        validFlagVP.set(dateVal !== undefined || !isRequired);
    }

    return (
        <DateTimePicker
            variant={variant}
            title={title === LOADING ? "Loading…" : asString(title)}
            placeholder={placeholder === LOADING ? "Loading…" : asString(placeholder)}
            value={dateVal}
            pastAndOrFuture={(pastAndOrFutureValue as PastAndOrFuture | undefined) ?? PastAndOrFuture.All}
            onChange={onChange}
            isEnabled={isEnabled}
            isRequired={isRequired}
            appKind={env.appEnvironment.appKind}
        />
    );
};
